/**
 * AMQ Matratzenfinder — Quiz-Definition und Ergebniskarte.
 *
 * Portierung von wp-matratzenfinder/assets/js/matratzenfinder.js. Das Original brauchte
 * jQuery, Swiper, Video.js und ein Dialog-Polyfill; hier läuft alles ohne Abhängigkeiten.
 *
 * Der ganze Ablauf — Slides, Navigation, Fortschrittsleiste, Ladescreen, Produktabruf,
 * Warenkorb — steckt in `amq-finder-engine.js`, die sich Matratzen- und Topperfinder teilen.
 * Diese Datei ist das „Rezept": Fragen, Sonderregeln, r-Code und Ergebnis.
 *
 * Eigenheit dieses Finders ist der Partnerlauf: Ab 160 cm Breite lässt sich auf zwei
 * Personen umstellen, der Fragebogen läuft dann zweimal durch und die Fortschrittsleiste
 * bekommt zwei Abschnitte. Die Engine weiß davon nichts — sie fragt nur nach der
 * Schrittliste und lässt sich über `neuAufbauen()` neu aufbauen.
 *
 * Die Empfehlung kommt aus der API des automation-tools (nur Modell, Härtegrad und die
 * Shopify-Optionswerte), Preis und Variante holt das Theme direkt aus dem Shop.
 */
(() => {
  'use strict';

  /* ══════════════════════════════════════════════════════════════════════════
     1. QUIZ-DEFINITION

     Reihenfolge, Werte-Codes und Texte wie im Plugin. Die Codes stehen so auch im
     r-Code und damit in jedem geteilten Link — sie dürfen sich nie ändern.
     ══════════════════════════════════════════════════════════════════════════ */

  const BREITEN = [80, 90, 100, 120, 140, 160, 180, 200];
  const LAENGEN = [190, 200, 210, 220];
  /** Ab dieser Breite bietet das Plugin die Partnermatratze an. */
  const PARTNER_AB_BREITE = 160;

  const GRENZEN = {
    groesse: { min: 110, max: 220 },
    gewicht: { min: 20, max: 150 },
  };

  /** Kurzform für die Fortschrittsleiste — im alten Design standen dort die langen Namen. */
  const KURZNAMEN = {
    'Schlafposition Präferenz': 'Position',
    'Liegegefühl Präferenz': 'Liegegefühl',
    Schlafklima: 'Klima',
    Körpermerkmale: 'Körper',
    Besonderheiten: 'Attribute',
    'Angaben Partner/in': 'Partner/in',
  };

  /** Untertitel der Videokarte, je Schritt. */
  const VIDEO_TEXTE = [
    'Was ist der Matratzenfinder?',
    'Schlafpositionen',
    'Lieber fest oder weich?',
    'Bevorzugtes Schlafklima',
    'Körperliche Eigenschaften',
    'Besondere Eigenschaften',
    'Budget für Ihre Matratze',
  ];

  const personSchritte = (prefix, untertitel) => [
    {
      hash: `${prefix}schlafposition`,
      kurz: 'Schlafposition Präferenz',
      video: 'schlafposition',
      klasse: 'schlafpositon',
      untertitel,
      frage: 'Wie schlafen Sie am liebsten?',
      feld: `${prefix}liegeposition`,
      typ: 'radio',
      optionen: [
        { wert: 'W', info: 'wechselnde Liegeposition', bild: 'schlafposition-wechselnd', text: 'wechselnde Liegeposition' },
        { wert: 'S', info: 'Seite', bild: 'schlafposition-seite', text: 'Seitenlage' },
        { wert: 'B', info: 'Bauch', bild: 'schlafposition-bauch', text: 'Bauchlage' },
        { wert: 'R', info: 'Rücken', bild: 'schlafposition-ruecken', text: 'Rückenlage' },
      ],
    },
    {
      hash: `${prefix}liegegefuehl`,
      kurz: 'Liegegefühl Präferenz',
      video: 'liegegefuehl',
      klasse: 'liegegefuehl',
      untertitel,
      frage: 'Welches Liegegefühl bevorzugen Sie?',
      feld: `${prefix}liegegefuehl`,
      typ: 'radio',
      nurText: true,
      optionen: [
        { wert: 'W', info: 'weich', text: 'Weich' },
        { wert: 'M', info: 'mittel', text: 'Mittel' },
        { wert: 'F', info: 'fest', text: 'Fest' },
      ],
    },
    {
      hash: `${prefix}schlafklima`,
      kurz: 'Schlafklima',
      video: 'schlafklima',
      klasse: 'schlafklima',
      untertitel,
      frage: 'Welches Schlafklima wünschen Sie sich?',
      feld: `${prefix}schlafklima`,
      typ: 'radio',
      optionen: [
        { wert: 'K', info: 'eher kühl', bild: 'schlafklima-kalt', text: 'Eher kühl', zusatz: 'Für Personen die nachts schnell schwitzen' },
        { wert: 'N', info: 'neutral', bild: 'schlafklima-neutral', text: 'Neutral', zusatz: 'Für Personen die nachts nicht schnell frieren oder schwitzen' },
        { wert: 'W', info: 'eher warm', bild: 'schlafklima-warm', text: 'Eher warm', zusatz: 'Für Personen die nachts schnell frieren' },
      ],
    },
    {
      hash: `${prefix}koerper`,
      kurz: 'Körpermerkmale',
      video: 'koerper',
      klasse: 'koerper validate',
      untertitel,
      frage: 'Ihre Körpermaße',
      typ: 'koerper',
      prefix,
      zweiteFrage: 'Ihr Körpertyp',
      feld: `${prefix}koerperform`,
      optionen: [
        { wert: 'H', info: 'Eher schwer, größerer Bauchumfang', bild: 'koerpertyp-h', text: 'Eher schwer, größerer Bauchumfang' },
        { wert: 'E', info: 'Schultern breiter als Hüfte & Becken', bild: 'koerpertyp-e', text: 'Schultern breiter als Hüfte & Becken' },
        { wert: 'I', info: 'Schultern & Becken ähnlich breit', bild: 'koerpertyp-i', text: 'Schultern & Becken ähnlich breit' },
        { wert: 'A', info: 'Schmale Schultern, breites Becken', bild: 'koerpertyp-a', text: 'Schmale Schultern, breites Becken' },
      ],
    },
    {
      hash: `${prefix}besonderheiten`,
      kurz: 'Besonderheiten',
      video: 'besonderheiten',
      klasse: 'besonderheiten',
      untertitel,
      frage: 'Trifft Folgendes auf Sie zu?',
      hinweis: '(Mehrfachauswahl möglich)',
      feld: `${prefix}besonderheiten`,
      typ: 'checkbox',
      optionen: [
        { wert: 'D', info: 'Druckempfindlichkeit', bild: 'person-druckempfindlich', text: 'Druckempfindlichkeit', zusatz: 'z.B. an Schulter, Hüfte oder Rippen' },
        { wert: 'K', info: 'Kribbeln', bild: 'person-kribbeln', text: 'Kribbeln/Einschlafen der Gliedmaßen' },
        { wert: 'A', info: 'Allergiker/in', bild: 'person-allergiker', text: 'Allergiker/in' },
        { wert: 'N', info: 'Keine', bild: 'person-nichts', text: 'Auf mich trifft nichts davon zu' },
      ],
    },
  ];

  /**
   * Erscheint, sobald jemand „Für mich & Partner*in" wählt: Der Fragebogen läuft dann
   * zweimal durch. Wortlaut und Knopfbeschriftung wie im Plugin.
   */
  /**
   * Die Köpfe über den Fragen im Partnerlauf. Wortlaut und Auszeichnung wie im Plugin;
   * beides sind feste Zeichenketten aus dieser Datei, deshalb gehen sie unescaped ins
   * Markup — nur so bleibt das <b> erhalten.
   */
  const UNTERTITEL_A = 'Angaben zu <b>meiner</b> Liegeseite';
  const UNTERTITEL_B = 'Angaben Liegeseite <b>Partner/in</b>';

  const PARTNER_HINWEIS =
    '<p>Da Sie eine Matratze für zwei Personen suchen, beantworten Sie bitte zunächst alle Fragen für sich. <br><br>Im Anschluss startet der Fragebogen ein zweites Mal für die Angaben zur zweiten Person.</p>' +
    '<button type="button" class="button-close slide-next">Daten für meine Liegeseite eingeben</button>';

  const BUDGET_SCHRITT = {
    hash: 'budget',
    kurz: 'Budget',
    video: 'budget',
    klasse: 'budget',
    frage: 'Worauf legen Sie Wert?',
    feld: 'budget',
    typ: 'radio',
    optionen: [
      { wert: 'B', info: 'das Beste', bild: 'budget-beste-qualitaet', text: 'Ich tendiere dazu immer das Beste zu kaufen' },
      { wert: 'L', info: 'Preis/Leistung', bild: 'budget-preis-leistung', text: 'Mein Fokus liegt auf Preis/Leistung' },
      { wert: 'P', info: 'Preis entscheidend', bild: 'budget-wenig-budget', text: 'Für mich ist der Preis entscheidend' },
    ],
  };

  const GROESSE_SCHRITT = { typ: 'groesse', hash: 'matratzengroesse', kurz: 'Matratzengröße', video: 'intro' };

  /** Der Partnerlauf ist der einzige Zustand, den dieses Rezept selbst hält. */
  let istPartner = false;

  const sessionId = crypto.randomUUID();
  // Erst beim Start gesetzt: Im Kiosk bekommt jeder Seitenaufruf eine eigene Kennung, statt sie
  // im Browser des Tablets zu merken.
  let userId;
  // Markt (für shop_name) und Showroom des Kiosk-Tablets gehen in jeder Meldung mit.
  let zusatz = {};
  const wartend = [];
  const videoStaende = new Map();
  let sendeTimer;
  let sendet = false;
  let trackingUrl;

  async function trackingSenden() {
    if (sendet || !wartend.length || !trackingUrl) return;
    sendet = true;
    const paket = wartend.splice(0, 10);
    try {
      const antwort = await fetch(trackingUrl, {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(paket),
      });
      if (!antwort.ok) throw new Error(`Tracking HTTP ${antwort.status}`);
    } catch (fehler) {
      console.warn('[Matratzenfinder] Tracking', fehler);
    } finally {
      sendet = false;
      if (wartend.length) sendeTimer = setTimeout(trackingSenden, 800);
    }
  }

  function trackingMelden(meldung) {
    if (!trackingUrl) return;
    wartend.push({ session_id: sessionId, user_id: userId, device: matchMedia('(max-width: 767px)').matches ? 'm' : 'd', ...zusatz, ...meldung });
    clearTimeout(sendeTimer);
    sendeTimer = setTimeout(trackingSenden, 500);
  }

  function trackingStarten(h) {
    if (h.kiosk) {
      userId = crypto.randomUUID();
    } else {
      try {
        userId = localStorage.getItem('amq_mf_user_id');
        if (!userId) localStorage.setItem('amq_mf_user_id', (userId = crypto.randomUUID()));
      } catch {
        userId = crypto.randomUUID();
      }
    }
    zusatz = {
      ...(h.KONFIG.markt ? { markt: h.KONFIG.markt } : {}),
      ...(h.showroom ? { showroom: h.showroom } : {}),
    };
    try {
      const url = new URL(h.KONFIG.apiUrl, location.origin);
      url.pathname = url.pathname.replace(/[^/]*$/, 'tracking');
      url.search = '';
      trackingUrl = url.href;
      trackingMelden({ type: 'slide', value: 0 });
    } catch (fehler) {
      console.warn('[Matratzenfinder] Tracking-URL', fehler);
    }
  }

  window.addEventListener('pagehide', () => {
    clearTimeout(sendeTimer);
    if (wartend.length && trackingUrl) {
      fetch(trackingUrl, {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(wartend.splice(0, 10)),
      }).catch(() => {});
    }
  });

  /*
   * Google: dieselben Events wie im Plugin (dort per gtag bzw. dataLayer). Im Shop gibt es
   * kein gtag — Google läuft über den Tag Manager (snippets/gtm_loader.liquid), der erst nach
   * der Einwilligung lädt und den dataLayer dann von vorn abarbeitet. Bis dahin liegen die
   * Events nur im Browser. Damit sie in GA4 ankommen, braucht der GTM-Container je Event
   * einen Trigger samt GA4-Tag.
   */
  // Das Plugin meldete 3.0 — die Shopify-Fassung zählt weiter, damit sich alt und neu trennen lassen.
  const GOOGLE_APP = { app_name: 'Matratzenfinder', app_version: '4.0' };
  let googleGestartet = false;
  /** Die angezeigte Empfehlung als GA4-Artikel, für den Klick auf „In den Warenkorb". */
  let googleArtikel = null;

  function googleMelden(daten) {
    window.dataLayer = window.dataLayer || [];
    // Wie das Pixel „GTM & dataLayer": Ein altes ecommerce-Objekt darf nicht ins neue durchschlagen.
    if (daten.ecommerce) window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ ...daten, ...GOOGLE_APP });
  }

  /** „Härtegrad 3" bzw. „Härtegrade 2 & 4" — so steht es im Banner und im Tracking. */
  function haertegradText(daten) {
    return daten.art === 'partner'
      ? `Härtegrade ${daten.primaer.haertegradA} & ${daten.primaer.haertegradB}`
      : `Härtegrad ${daten.primaer.haertegrad}`;
  }

  /* ══════════════════════════════════════════════════════════════════════════
     2. ERGEBNIS-BAUSTEINE

     Die kleinen Symbole liefert die Engine, die großen (Begründungen, Siegel,
     Garantie) liegen als Dateien in den Theme-Assets — ihre Adressen kommen aus der
     Konfiguration. Die Texte stehen im Original genauso fest im Template.
     ══════════════════════════════════════════════════════════════════════════ */

  const ERGEBNIS_TEXTE = {
    erfolg: 'Wir haben Ihre perfekte Matratze gefunden.',
    bildunterschrift: 'Ihr individuelles Ergebnis auf Basis von 5.000+ persönlichen Kundenberatungen',
    gruendeKopf: 'Warum diese Matratze perfekt zu Ihnen passt',
    kunden: '250.000 Zufriedene Kunden',
    garantie:
      'Ihre Wahl soll sich langfristig richtig anfühlen. Deshalb helfen wir jederzeit weiter – mit 10 Jahren Garantie.',
    warenkorb: 'In den Warenkorb',
    steuer: '(inkl. MwSt.)',
    sliderKopf: 'Überzeugen Sie sich selbst von der',
  };

  /** Reihenfolge der Begründungen wie im Original; die letzten beiden tragen dort eine ID. */
  const GRUND_REIHENFOLGE = [
    { schluessel: 'haertegrad' },
    { schluessel: 'liegeposition' },
    { schluessel: 'schlafklima' },
    { schluessel: 'budget', id: 'budget_bullet_point' },
    { schluessel: 'druckempfindlichkeit', id: 'druckempfindlichkeit_bullet_point' },
  ];

  /**
   * „Überzeugen Sie sich selbst von der …" — im Original steht dort die Warengruppe. Die gibt es
   * in Shopify nicht (alle Matratzen haben den Typ „Matratze"), also nehmen wir das letzte
   * Wort des Produktnamens, das auf „matratze" endet: aus „1000 Federn 7-Zonen
   * Taschenfederkernmatratze" wird „Taschenfederkernmatratze".
   */
  function warengruppe(name) {
    const woerter = String(name).split(/\s+/);
    for (let i = woerter.length - 1; i >= 0; i--) {
      if (/matratze$/i.test(woerter[i])) return woerter[i];
    }
    return name;
  }

  /* ══════════════════════════════════════════════════════════════════════════
     3. DAS REZEPT
     ══════════════════════════════════════════════════════════════════════════ */

  window.AmqFinder.starten({
    name: 'Matratzenfinder',
    trackingSession: () => sessionId,
    beiStart: (h) => trackingStarten(h),
    beiSchrittAbschluss(schritt, index, h) {
      const prefix = schritt.hash.startsWith('personB_') ? 'personB_' : 'personA_';
      let eingabe;
      if (schritt.hash === 'matratzengroesse') {
        eingabe = [h.wert('matratzen_breite'), h.wert('matratzen_laenge'), h.gewaehlt('matratzen_art')].join('|');
      } else if (schritt.typ === 'koerper') {
        eingabe = [h.wert(`${prefix}groesse`), h.wert(`${prefix}gewicht`), h.gewaehlt(schritt.feld)].join('|');
      } else if (schritt.typ === 'checkbox') {
        eingabe = h.gewaehlteWerte(schritt.feld).join('|');
      } else {
        eingabe = h.gewaehlt(schritt.feld);
      }
      trackingMelden({ type: 'slide', value: index + 1 + (schritt.hash.startsWith('personB_') ? 1 : 0), slide_name: schritt.hash, user_input: eingabe });

      // Plugin: „Seite N von M" bei jedem Schritt vorwärts, N ist der neue Schritt ab 1.
      // Der letzte Schritt führt zum Ergebnis — das meldet `beiErgebnis`.
      if (index < h.schritte.length - 1) {
        googleMelden({ event: 'screen_view', screen_name: `Seite ${index + 2} von ${h.schritte.length}` });
        if (index === 0 && !googleGestartet) {
          googleGestartet = true;
          googleMelden({
            event: 'matratzenfinder_start',
            event_category: 'engagement',
            event_label: 'Startseite',
            screen_name: 'Matratzenfinder Start',
          });
        }
      }
    },
    beiErgebnis(daten, { produkt, variante }, { geteilt }, h) {
      const name = produkt?.title ?? daten.primaer.name;
      const ausfuehrung = `${daten.masse.breite} x ${daten.masse.laenge} cm, ${haertegradText(daten)}`;
      const preis = Number.isFinite(variante?.price) ? variante.price / 100 : undefined;
      googleArtikel = variante
        ? {
            item_id: variante.sku || String(variante.id),
            item_name: name,
            variant: ausfuehrung,
            // variant_id als Text und der Produkttyp als Kategorie — wie gtm_add_to_cart des Pixels.
            variant_id: String(variante.id),
            item_category: produkt?.type || 'Matratze',
            price: preis,
          }
        : null;
      // Wie im Plugin auch für geteilte Links; die Ergebnisseite dagegen nur nach eigenem Quiz.
      googleMelden({
        event: 'matratzenfinder_complete',
        mattress_name: name,
        mattress_ean: variante?.sku ?? '',
        mattress_dimensions: ausfuehrung,
        mattress_price: preis,
        currency: h.KONFIG.geldWaehrung,
      });
      if (!geteilt) {
        googleMelden({
          event: 'screen_view',
          screen_name: `Ergebnisseite: ${daten.art === 'partner' ? 'Partnermatratze' : 'Singlematratze'}`,
        });
      }
    },
    /**
     * Im Plugin hieß das Event „add_to_cart". Hier meldet den Warenkorb schon das Pixel
     * „GTM & dataLayer" als gtm_add_to_cart — unter gleichem Namen zählte GA4 ihn doppelt.
     */
    beiWarenkorb(varianteId, h) {
      if (!googleArtikel || googleArtikel.variant_id !== String(varianteId)) return;
      googleMelden({
        event: 'matratzenfinder_add_to_cart',
        ecommerce: {
          currency: h.KONFIG.geldWaehrung,
          value: googleArtikel.price,
          items: [{ ...googleArtikel, quantity: 1 }],
        },
      });
    },
    beiVideoFortschritt(video, prozent) {
      if (!video) return;
      for (const meilenstein of [5, 25, 50, 75, 100]) {
        const schluessel = `${video}:${meilenstein}`;
        if (prozent >= meilenstein && !videoStaende.has(schluessel)) {
          videoStaende.set(schluessel, true);
          trackingMelden({ type: 'video', value: meilenstein, label: video });
        }
      }
    },
    /** Präfix der Fehlerreferenz, die der Besucher zu sehen bekommt. */
    kuerzel: 'MF',
    wurzelAttribut: 'data-mf-root',
    konfigAttribut: 'data-mf-config',
    containerId: 'matratenfinder-container',
    finderId: 'matratzen-finder',
    fortschrittId: 'mf-progress',
    speicher: 'amq_mf_data',
    neustartText: 'Matratzenfinder neu starten',
    bannerZiel: '.matratzengroesse .mf-body',
    kurznamen: KURZNAMEN,
    videoTexte: VIDEO_TEXTE,
    hinweisKopf: 'Für diese Kombination haben wir keine Standardempfehlung',
    hinweisText: 'Melden Sie sich gern bei uns — wir beraten Sie persönlich und finden die passende Matratze für Sie.',

    schritteBauen() {
      const liste = [GROESSE_SCHRITT];
      if (istPartner) {
        liste.push(...personSchritte('personA_', UNTERTITEL_A));
        liste.push(...personSchritte('personB_', UNTERTITEL_B));
      } else {
        liste.push(...personSchritte('personA_', ''));
        liste.push(BUDGET_SCHRITT);
      }
      return liste;
    },

    /** Zwei Schrittarten, die die Engine nicht kennt: die Maße und der Körper-Schritt. */
    slideHtml(schritt, h) {
      const { esc, icon, kachel } = h;

      if (schritt.typ === 'groesse') {
        const opt = (werte, gewaehlt) =>
          werte.map((w) => `<option value="${w}"${w === gewaehlt ? ' selected' : ''}>${w} cm</option>`).join('');
        return `
<div class="swiper-slide matratzengroesse" data-hash="matratzengroesse" data-shortname="Matratzengröße" data-video="intro">
  <div class="mf-body">
    <h2>Welche Größe soll Ihre Matratze haben?</h2>
    <div class="flex-container">
      <div class="hg-form-group">
        <label for="matratzen_breite">Breite</label>
        <select class="hg-form-control" name="matratzen_breite" id="matratzen_breite">${opt(BREITEN, 90)}</select>
      </div>
      <div class="hg-form-group">
        <label for="matratzen_laenge">Länge</label>
        <select class="hg-form-control" name="matratzen_laenge" id="matratzen_laenge">${opt(LAENGEN, 200)}</select>
      </div>
      <div class="bild_masse">
        <img width="250" src="${icon('matratzenmasse')}" alt="" aria-hidden="true">
      </div>
    </div>
    <div class="option_partner hidden">
      <h3>Für wen suchen Sie eine Matratze?</h3>
      <div class="flex-container">
        <label>
          <input type="radio" name="matratzen_art" value="single" data-shortinfo="" checked>
          <picture decoding="async"><img src="${icon('matratze-single')}" alt="Für mich" width="150" height="150"></picture>
          <div class="description"><span>Für mich</span></div>
        </label>
        <label>
          <input type="radio" name="matratzen_art" value="partner" data-shortinfo="">
          <picture decoding="async"><img src="${icon('matratze-partner')}" alt="Für mich &amp; Partner/in" width="150" height="150"></picture>
          <div class="description"><span>Für mich &amp; Partner*in</span></div>
        </label>
      </div>
    </div>
  </div>
</div>`;
      }

      if (schritt.typ === 'koerper') {
        return `
<div class="swiper-slide ${schritt.klasse}" data-hash="${schritt.hash}" data-shortname="${esc(schritt.kurz)}" data-video="${schritt.video}">
  ${schritt.untertitel ? `<div class="mf-sub-header"><h3>${schritt.untertitel}</h3></div>` : ''}
  <div class="mf-body">
    <h2>${esc(schritt.frage)}</h2>
    <div class="flex-container">
      <div class="hg-form-group">
        <label for="${schritt.prefix}groesse">Ihre Größe [cm]</label>
        <input type="number" inputmode="numeric" class="hg-form-control" id="${schritt.prefix}groesse" name="${schritt.prefix}groesse" placeholder="Größe [in cm]" min="${GRENZEN.groesse.min}" max="${GRENZEN.groesse.max}" required>
      </div>
      <div class="hg-form-group">
        <label for="${schritt.prefix}gewicht">Ihr Gewicht [kg]</label>
        <input type="number" inputmode="numeric" class="hg-form-control" id="${schritt.prefix}gewicht" name="${schritt.prefix}gewicht" placeholder="Gewicht [in kg]" min="${GRENZEN.gewicht.min}" max="${GRENZEN.gewicht.max}" required>
      </div>
    </div>
    <h3>${esc(schritt.zweiteFrage)}</h3>
    <div class="flex-container">${schritt.optionen.map((o) => kachel(schritt, o)).join('')}</div>
  </div>
</div>`;
      }

      return null;
    },

    /**
     * Größe und Gewicht, bevor die allgemeine Frage kommt, ob überhaupt etwas gewählt ist.
     *
     * Eine Abweichung vom Plugin: Dort wird nur bei Person A geprüft, ob die Felder
     * überhaupt gefüllt sind. Bei der zweiten Person landet ein leeres Feld in der Prüfung
     * auf „unter 110 cm" und bekommt die Kindermatratzen-Meldung zu sehen. Das ist ein
     * Versehen und hier für beide Personen gleich gelöst.
     */
    pruefung(schritt, slide, h) {
      if (schritt.typ !== 'koerper') return null;
      const groesse = slide.querySelector(`[name="${schritt.prefix}groesse"]`)?.value ?? '';
      const gewicht = slide.querySelector(`[name="${schritt.prefix}gewicht"]`)?.value ?? '';
      if (groesse === '' || gewicht === '') return '<p>Bitte geben Sie Ihr Gewicht/Ihre Größe an.</p>';
      if (Number(groesse) < GRENZEN.groesse.min || Number(gewicht) < GRENZEN.gewicht.min) {
        const link = h.KONFIG.babyUrl
          ? ` <a class="mf-link" href="${h.esc(h.KONFIG.babyUrl)}">Babymatratze</a>`
          : ' Babymatratze';
        return `<p>Aufgrund Ihrer angegebenen Körpergröße bzw. Ihres Gewichts gehen wir davon aus, dass Sie eine Matratze für ein Kind suchen. In diesem Fall empfehlen wir Ihnen unsere${link}. Falls dies nicht zutrifft, überprüfen Sie bitte Ihre Eingaben und passen Sie diese gegebenenfalls an.</p>`;
      }
      if (Number(gewicht) > GRENZEN.gewicht.max) {
        return `<p>Unsere Matratzen sind leider nur für Personen mit einem maximalen Körpergewicht von ${GRENZEN.gewicht.max} kg konzipiert.</p>`;
      }
      if (Number(groesse) > GRENZEN.groesse.max) {
        return `<p>Unsere Matratzen sind leider nur für Personen mit einer maximalen Körpergröße von ${GRENZEN.groesse.max} cm konzipiert.</p>`;
      }
      return null;
    },

    /** Breite steuert, ob die Partneroption überhaupt angeboten wird. */
    nachAufbau(h) {
      partnerOptionUmschalten(h);
    },

    beiAenderung(ziel, h) {
      if (ziel.name === 'matratzen_breite') partnerOptionUmschalten(h);
      if (ziel.name === 'matratzen_art') {
        const neuPartner = ziel.value === 'partner';
        if (neuPartner !== istPartner) {
          istPartner = neuPartner;
          // Der Neuaufbau ersetzt auch den Dialog — die Meldung kommt deshalb danach.
          h.neuAufbauen();
          if (neuPartner) h.meldungZeigen(PARTNER_HINWEIS);
          return true;
        }
      }
      return false;
    },

    /* ── Fortschrittsleiste: zwei Abschnitte im Partnerlauf ────────────────── */

    abschnittVor(schritt, kopf) {
      if (!istPartner) return '';
      if (schritt.hash === 'personA_schlafposition') return kopf('personA_section active', 'personA_header', 'Meine Angaben', 'personA');
      if (schritt.hash === 'personB_schlafposition') return `</div></div>${kopf('personB_section', 'personB_header', 'Angaben Partner/in', 'personB')}`;
      return '';
    },
    abschnittEndetNach: (schritt) => istPartner && schritt.hash === 'personA_besonderheiten',
    abschnittAmEnde: () => (istPartner ? '</div></div>' : ''),
    nachFortschritt(pagination) {
      if (!istPartner) return;
      // Trennlinie zwischen den beiden Personen — im Plugin fügt sie der Patch nachträglich ein.
      pagination.querySelector('.personA_section')?.insertAdjacentHTML('afterend', '<div class="am6m-persons-divider line-between"></div>');
    },
    /**
     * Hakt den Kopf eines Abschnitts ab, sobald alle Schritte der Person beantwortet
     * sind. Das Plugin tat das nur für Person A — der Kopf von Person B blieb dadurch
     * ohne Zustand. Solange beide Köpfe ohnehin dauerhaft dunkel aussahen, fiel das
     * nicht auf; jetzt, wo sie den offenen Zustand zeigen, bräuchte Person B sonst
     * ewig „noch offen".
     */
    nachFortschrittAktualisierung(pagination, fertig, schritte) {
      for (const person of ['personA', 'personB']) {
        const alleFertig = schritte.every((s, i) => !s.hash?.startsWith(`${person}_`) || fertig[i]);
        pagination.querySelector(`.${person}_header`)?.classList.toggle('correct', alleFertig);
      }
    },

    /**
     * `changeMenuSlide` des Plugins: In der Leiste ist immer nur der Abschnitt der Person
     * aufgeklappt, bei der man gerade steht — beide Reihen liegen übereinander, sichtbar ist
     * die des Abschnitts mit `active`. Ohne das blieb die Reihe von Person A stehen: Auf
     * Schritt 1 von Person B sah die Leiste deshalb schon vollständig abgehakt aus.
     *
     * Die 150 ms entsprechen dem Plugin — erst fährt die alte Reihe zu, dann die neue auf.
     */
    beiSchrittwechsel(h) {
      if (!istPartner) return;
      const pagination = h.wurzel.querySelector('#mf-progress .swiper-pagination');
      const beiPartner = (h.schritte[h.index]?.hash ?? '').startsWith('personB_');
      const abschnittA = pagination.querySelector('.personA_section');
      const abschnittB = pagination.querySelector('.personB_section');
      (beiPartner ? abschnittA : abschnittB)?.classList.remove('active');
      window.setTimeout(() => (beiPartner ? abschnittB : abschnittA)?.classList.add('active'), 150);
    },

    /* ── r-Code ────────────────────────────────────────────────────────────────
       Bitgleich zum Plugin: base64 über die mit `|` verbundenen Rohwerte. Das Format
       steckt in jedem geteilten Link und in alten E-Mails — es darf sich nicht ändern.
       ──────────────────────────────────────────────────────────────────────── */

    rCode(h) {
      const personTeile = (prefix) => [
        h.wert(`${prefix}groesse`),
        h.wert(`${prefix}gewicht`),
        h.gewaehlt(`${prefix}koerperform`),
        h.gewaehlt(`${prefix}liegeposition`),
        h.gewaehlt(`${prefix}liegegefuehl`),
        h.gewaehlt(`${prefix}schlafklima`),
        h.werte(`${prefix}besonderheiten`),
      ];
      const masse = [h.wert('matratzen_breite'), h.wert('matratzen_laenge')];
      const felder = istPartner
        ? [...masse, ...personTeile('personA_'), ...personTeile('personB_')]
        : [...masse, ...personTeile('personA_'), h.gewaehlt('budget')];
      // btoa erwartet Latin-1; die Rohwerte sind reine ASCII-Codes, deshalb genügt das.
      return btoa(felder.join('|'));
    },

    /* ── Ergebnis ──────────────────────────────────────────────────────────── */

    async ergebnisAufbereiten(daten, h) {
      const e = daten.primaer;
      if (!e.shopify) {
        h.problemMelden({
          art: 'warnung',
          phase: 'modell',
          meldung: `Modell ${e.modell} hat keine Shopify-Zuordnung`,
          code: daten.code,
        });
        return { produkt: null, variante: null };
      }
      const produkt = await h.produktHolen(e.shopify.handle);
      const variante = h.varianteFinden(produkt, e.shopify.optionen);
      // Ohne Variante steht die Karte ohne Preis und ohne Warenkorb da — sie sieht heil
      // aus, ist es aber nicht. Anders als beim Topperfinder ist das hier kein Randfall,
      // sondern immer ein Defekt: jede empfohlene Größe muss es geben.
      if (!variante) {
        h.problemMelden({
          art: 'warnung',
          phase: 'variante',
          meldung: `Variante nicht auffindbar: ${e.shopify.optionen.map((o) => `${o.name}=${o.value}`).join(', ')}`,
          handle: e.shopify.handle,
          code: daten.code,
        });
      }
      // Wie auf der Produktseite für genau diese Variante: Die Bearbeitungszeit hängt an der Größe.
      const lieferzeit = variante ? await h.lieferzeitHolen(e.shopify.handle, variante.id) : '';
      return { produkt, variante, lieferzeit };
    },

    ergebnisHtml(daten, { produkt, variante, lieferzeit }, code, h) {
      const { esc, geld, sterneHtml, HAKEN_GRUEN_SVG, PUNKT_GRUEN_SVG, CHEVRON_LINKS_SVG, CHEVRON_RECHTS_SVG } = h;
      const e = daten.primaer;
      const handle = e.shopify?.handle ?? '';
      const info = h.KONFIG.produkte?.[handle] ?? {};
      const symbole = h.KONFIG.ergebnis ?? {};
      const name = produkt?.title ?? e.name;

      const haertegrad = daten.art === 'partner' ? `H${e.haertegradA} & H${e.haertegradB}` : `H${e.haertegrad}`;
      const kennwerte = [
        ['Breite:', `${daten.masse.breite} cm`],
        ['Länge:', `${daten.masse.laenge} cm`],
        [daten.art === 'partner' ? 'Härtegrade:' : 'Härtegrad:', haertegrad],
      ]
        .map(([links, rechts]) => `<div class="spec-box"><div class="prev">${esc(links)}</div><div class="nxt">${esc(rechts)}</div></div>`)
        .join('');

      const gruende = GRUND_REIHENFOLGE.filter((g) => daten.texte?.[g.schluessel])
        .map(
          (g) => `
      <div class="mf-result__feature-item"${g.id ? ` id="${g.id}"` : ''}>
        <div class="mf-result__feature-icon"><img src="${esc(symbole.gruende?.[g.schluessel] ?? '')}" alt="" loading="lazy"></div>
        <div class="mf-result__feature-text">${daten.texte[g.schluessel]}</div>
      </div>`,
        )
        .join('');

      // Ohne Bewertungsdaten am Produkt bleibt die Zeile weg, statt null Sterne zu zeigen.
      const bewertung =
        info.anzahl > 0 && info.bewertung > 0
          ? `
      <div class="mf-result__reviews">
        ${sterneHtml(Number(info.bewertung), Number(info.bewertungMax))}
        <a href="${esc(info.url || `/products/${handle}`)}#reviews" rel="nofollow" data-amq-bewertungen="${esc(handle)}"><span class="count">${esc(info.anzahl)}</span> ${info.anzahl === 1 ? 'Rezension' : 'Rezensionen'}</a>
      </div>`
          : '';

      const avatare = (symbole.avatare ?? [])
        .map((quelle) => `<div class="mf-result__avatar"><img src="${esc(quelle)}" alt="" loading="lazy" width="92" height="92"></div>`)
        .join('');

      const siegel = (symbole.siegel ?? [])
        .map((s) => `<div class="mf-result__trust-item"><img src="${esc(s.bild)}" alt="" loading="lazy"><p>${s.text}</p></div>`)
        .join('');

      // „(inkl. MwSt.) kostenloser Versand in die Schweiz" — das Versandziel setzt die Section je Markt.
      const preisHinweis = [ERGEBNIS_TEXTE.steuer, h.KONFIG.versandHinweis].filter(Boolean).join(' ');
      const preis = variante
        ? `<p class="preis"><span class="amount">${geld(variante.price)}</span> <span class="tax-info tax-info--versand">${esc(preisHinweis)}</span></p>`
        : '';

      const warenkorb = variante?.available
        ? `<button type="button" class="amq_mf_add_to_cart" data-variant="${variante.id}"><span class="mf-cta-text">${ERGEBNIS_TEXTE.warenkorb}</span><span class="mf-cta-spinner" aria-hidden="true"></span></button>`
        : `<a class="amq_mf_add_to_cart" href="${esc(info.url || `/products/${handle}`)}">Zum Produkt</a>`;

      // Oben das Ergebnisbild aus dem Metafeld (in WP das erste Bild der Finder-Galerie),
      // ohne Metafeld das Produkt-Hauptbild. Der Streifen darunter zeigt die Produktbilder,
      // außer dem, das schon oben steht.
      const bilder = produkt?.images ?? [];
      const hauptbild = info.ergebnisbild || produkt?.featured_image || bilder[0] || '';
      const streifenBilder = bilder.filter((b) => b !== hauptbild);
      const streifen = streifenBilder
        .map((b) => `<div class="swiper-slide"><img src="${esc(b)}" alt="" loading="lazy"></div>`)
        .join('');
      // Ein Punkt je Bild, nur auf dem Handy zu sehen: zeigt, dass es weitergeht und wo man steht.
      const punkte =
        streifenBilder.length > 1
          ? streifenBilder
              .map(
                (_, i) =>
                  `<button type="button" class="mf-slider-punkt" aria-label="Bild ${i + 1} von ${streifenBilder.length}" aria-current="${i === 0}"></button>`,
              )
              .join('')
          : '';

      return `
<div class="amq-tpl-mf-result" data-mf-code="${esc(code)}" data-mf-handle="${esc(handle)}">
  <div class="mf-result__grid">
    <div class="mf-result__gallery">
      <div class="mf-result__gallery-wrapper">
        <div class="mf-result__main-image">
          ${hauptbild ? `<img src="${esc(hauptbild)}" alt="${esc(name)}">` : ''}
        </div>
        <div class="mf-result__gallery-overlay"><span>${ERGEBNIS_TEXTE.bildunterschrift}</span></div>
      </div>
    </div>

    <div class="mf-result__success-msg">
      <span class="mf-result__success-icon">${HAKEN_GRUEN_SVG}</span>
      <span class="mf-result__success-text">${ERGEBNIS_TEXTE.erfolg}</span>
    </div>

    <div class="mf-result__right">
      <div class="mf-result__content">
        ${bewertung}
        <h2 class="mf-result__product-name">${esc(name)}</h2>
        <div class="mf-result__specs">
          <div class="mf-result__specs-inner">${kennwerte}</div>
        </div>
        <div class="mf-result__cms-headline"><p>${ERGEBNIS_TEXTE.gruendeKopf}</p></div>
        <div class="mf-result__features">${gruende}</div>
        <div class="mf-result__customers">
          <div class="mf-result__customer-row">
            <div class="mf-result__avatars">${avatare}</div>
            <div class="mf-result__customer-count">${ERGEBNIS_TEXTE.kunden}</div>
          </div>
          <div class="mf-result__guarantee">
            <span>${ERGEBNIS_TEXTE.garantie}</span>
            <div class="mf-result__guarantee-badge warranty-badge-desktop">
              <img src="${esc(symbole.garantieBadge ?? '')}" alt="" loading="lazy">
            </div>
          </div>
        </div>
        <div class="mf-result__price">${preis}</div>
        <div class="mf-result__cta">${warenkorb}</div>
        ${
          lieferzeit
            ? `<div class="mf-result__delivery">
          <span class="mf-result__delivery-dot">${PUNKT_GRUEN_SVG}</span>
          <span class="mf-result__delivery-text"><p>${esc(lieferzeit)}</p></span>
        </div>`
            : ''
        }
        <div class="mf-result__subfooter">${siegel}</div>
      </div>
    </div>
  </div>

  ${
    streifen
      ? `<div class="mf-result__slider-section">
    <div class="mf-result__slider-title">
      <h2>${ERGEBNIS_TEXTE.sliderKopf} <span class="mf-result__slider-product-name">${esc(warengruppe(name))}</span></h2>
    </div>
    <div class="mf-slider-rahmen">
      <div class="mf-result__slider-body">
        <div class="swiper matratzenfinder--slider">
          <div class="swiper-wrapper">${streifen}</div>
        </div>
      </div>
      <button type="button" class="mf-slider-pfeil mf-slider-pfeil--zurueck" aria-label="Vorherige Bilder" hidden>${CHEVRON_LINKS_SVG}</button>
      <button type="button" class="mf-slider-pfeil mf-slider-pfeil--vor" aria-label="Weitere Bilder" hidden>${CHEVRON_RECHTS_SVG}</button>
    </div>
    ${punkte ? `<div class="mf-slider-punkte">${punkte}</div>` : ''}
  </div>`
      : ''
  }
</div>`;
    },

    letztesErgebnis(daten, { produkt, variante }, code, h) {
      return {
        name: daten.primaer.name,
        masse: `${daten.masse.breite} x ${daten.masse.laenge} cm`,
        zusatz: haertegradText(daten),
        bild: produkt?.featured_image ?? '',
        preisCent: variante?.price ?? null,
        // Für den Block auf der Startseite (amq_content_finder_ergebnis): Er verlinkt direkt
        // die Variante und holt den aktuellen Preis nach.
        handle: produkt?.handle ?? '',
        variante: variante?.id ?? null,
        link: `${h.KONFIG.seitenUrl}?r=${encodeURIComponent(code)}`,
      };
    },
  });

  /** Unter 160 cm gibt es keine Partnermatratze — dann verschwindet die Auswahl wieder. */
  function partnerOptionUmschalten(h) {
    const block = h.container.querySelector('.option_partner');
    const bild = h.container.querySelector('.bild_masse');
    if (!block) return;
    const breite = Number(h.container.querySelector('[name="matratzen_breite"]').value);
    const erlaubt = breite >= PARTNER_AB_BREITE;
    block.classList.toggle('hidden', !erlaubt);
    bild?.classList.toggle('hidden', erlaubt);
    if (!erlaubt && istPartner) {
      istPartner = false;
      h.antworten.set('matratzen_art', 'single');
      h.neuAufbauen();
    }
  }
})();
